/**
 * The little company square shown next to a name.
 *
 * Logos come from logo.dev, which can look a company up two ways:
 *   1. by domain  — /{domain}      (accurate, preferred)
 *   2. by name    — /name/{name}   (used when we have no website)
 *
 * If neither works, or no key is configured, we draw coloured initials instead,
 * so a row never shows a broken image.
 */

import { useEffect, useMemo, useState } from "react";

const LOGO_DEV_KEY = import.meta.env.VITE_LOGO_DEV_KEY ?? "";

/** Stable per-name colour, so the same company always gets the same square. */
const FALLBACK_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0d9488",
  "#0284c7",
  "#4f46e5",
];

function colorForName(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) % 997;
  }
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
}

function initialsFor(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((word) => word[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function domainFrom(url: string | null | undefined): string | null {
  if (!url || url === "NULL" || url === "—") return null;
  try {
    const withProtocol = url.startsWith("http") ? url : `https://${url}`;
    return new URL(withProtocol).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
}

type CompanyLogoProps = {
  name: string;
  website?: string | null;
  /** Rendered size in CSS pixels. */
  size?: number;
};

export function CompanyLogo({ name, website, size = 22 }: CompanyLogoProps) {
  const [failed, setFailed] = useState(false);

  // Retry when the row changes, otherwise one failure would stick.
  useEffect(() => setFailed(false), [name, website]);

  // Ask for roughly size × device pixel ratio so logos stay sharp on Retina.
  const requestSize = useMemo(() => {
    const ratio = Math.min(window.devicePixelRatio || 2, 3);
    return Math.min(256, Math.max(64, Math.round(size * ratio)));
  }, [size]);

  const domain = domainFrom(website);
  const trimmedName = name?.trim() ?? "";

  let source: string | null = null;
  if (LOGO_DEV_KEY && !failed) {
    const query = `token=${LOGO_DEV_KEY}&size=${requestSize}&format=png`;
    if (domain) source = `https://img.logo.dev/${domain}?${query}`;
    else if (trimmedName)
      source = `https://img.logo.dev/name/${encodeURIComponent(trimmedName)}?${query}`;
  }

  if (!source) {
    return (
      <span
        aria-hidden
        className="flex shrink-0 items-center justify-center rounded border border-slate-200 font-semibold text-white"
        style={{
          width: size,
          height: size,
          fontSize: size * 0.4,
          backgroundColor: colorForName(trimmedName),
        }}
      >
        {initialsFor(trimmedName)}
      </span>
    );
  }

  return (
    <img
      src={source}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="shrink-0 rounded border border-slate-200 bg-white object-contain"
      style={{ width: size, height: size }}
    />
  );
}

/** Logo plus name, the layout used in the first column of most tables. */
export function CompanyCell({
  name,
  website,
}: {
  name: string;
  website?: string | null;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <CompanyLogo name={name} website={website} />
      <span className="min-w-0 truncate font-medium text-slate-900" title={name || undefined}>
        {name || "—"}
      </span>
    </span>
  );
}
